import { AspspListItem } from '@tenant/components/AspspListItem';
import { useEffect, useReducer, useState } from 'react';
import { useParamQuery } from 'src/common/hooks/useParamQuery';
import Logo from '@tenant/components/Logo';
import Footer from '@tenant/components/Footer';
import { range } from 'ramda';
import { AspspListItemSkeleton } from 'src/pages/AspspSelectPage/components/AspspListItemSkeleton';
import { Loader } from 'src/common/components/Loader';
import { HealthStatus } from 'src/pages/AspspSelectPage/aspspModels';
import { useHistory } from 'react-router';
import { ScreenScrape } from 'src/pages/AspspSelectPage/ScreenScrape';
import {
  VerificationStatus,
  VerificationType,
} from 'src/common/models/verificationModels';
import { openConnectorParam } from 'src/pages/VerificationPage/components/VerificationPageFlow';
import { useStatusOfVerificationProcessQuery } from 'src/common/components/RequireCorrectStep/useProcessStatusQuery';
import { useVerificationId } from 'src/common/hooks/useVerificationId';
import { LoaderPageRoute, verificationIdParam } from 'src/routes';
import { useSetDocumentTitle } from 'src/common/hooks/useSetDocumentTitle';
import {
  useAspspListQuery,
  useGetOpenBankingUrlQuery,
  useSendAspspMutation,
} from 'src/pages/AspspSelectPage/aspspQueries';
import {
  ActionType,
  aspspReducer,
  initialAspspState,
} from 'src/pages/AspspSelectPage/aspspReducer';
import {
  AgreementWrapper,
  SecondaryText,
  SmallHeading,
  TestWrapper,
} from 'src/pages/AspspSelectPage/index.styled';
import {
  AspspLayoutContainer as LayoutContainer,
  ListWrapper,
  StyledCTAButton,
} from '@tenant/pages/AspspSelectPage/index.styled';
import { environment } from 'src/config/environment';
import { useSdkAspspHandler } from 'src/pages/AspspSelectPage/webSdk/useSdkAspspHandler';
import { FormattedMessage } from 'react-intl';
import Heading from '@tenant/components/Heading';
import AspspControlWrapper from '@tenant/components/AspspControlWrapper';
import { useHasIntlMessage } from 'src/common/hooks/useHasIntlMessage';
import AspspSdkVerificationLoader from '@tenant/components/AspspSdkVerificationLoader';
import AspspAlert from '@tenant/components/AspspAlert';
import AspspSectionSeparator from '@tenant/components/AspspSectionSeparator';
import AspspPageContent from '@tenant/components/AspspPageContent';
import BackToPartnerPageButton from '@tenant/components/BackToPartnerPageButton';
import { useQueryClient } from '@tanstack/react-query';
import { verificationQueryKeys } from 'src/common/verificationQueryKeys';
import UnknownBankWarning from './unknownBankWarning';
import {useFrontendTypeParam} from "../../common/hooks/useFrontendType";

const ipkoId = '00af3730-942a-4f20-b55f-a55212bd875b';
export const unknownBankId = '35a033c7-5994-4842-b70d-ee704208a1b8';

const AspspSelectPage = () => {
  useSetDocumentTitle('Aisgateway- Choosing a bank');

  const { isLoginProcessing, setIsLoginProcessing, setOpenedWindow } =
    useSdkAspspHandler();

  const history = useHistory();
  const verificationId = useVerificationId();
  const hasIntlMessage = useHasIntlMessage();
  const queryClient = useQueryClient();
  const frontType = useFrontendTypeParam();

  if (!verificationId) {
    history.replace('404');
  }

  useEffect(() => {
    localStorage.setItem(verificationIdParam, verificationId!);
  }, []);

  const [
    {
      selectedAspsp,
      isScreenScrapeModalOpen,
      isUserReturningToProcess,
      processData,
    },
    dispatch,
  ] = useReducer(aspspReducer, initialAspspState);

  const [openBankingUrlQueryEnabled, setOpenBankingUrlQueryEnabled] =
    useState(false);
  const [isProcessingAspspSelect, setIsProcessingAspspSelect] = useState(false);
  const [unknownBankPopupVisible, setIsUnknownBankPopupVisible] = useState(false);

  const paramQuery = useParamQuery();

  const aspspList = useAspspListQuery(verificationId);
  const processStatus = useStatusOfVerificationProcessQuery(verificationId);
  const openBankingUrl = useGetOpenBankingUrlQuery({
    verificationId,
    enabled: openBankingUrlQueryEnabled,
  });
  const sendAspspMutation = useSendAspspMutation();

  useEffect(() => {
    if (aspspList.data && openBankingUrl.data) {
      if (openBankingUrl.data.connectorType === VerificationType.Scrapper) {
        // IPKO hardcoded for screen scraping in current implementation
        const ipko = aspspList.data.find((x) => x.id === ipkoId);

        ipko &&
          dispatch({
            type: ActionType.RedirectToScreenScrape,
            payload: {
              isScreenScrapeModalOpen: true,
              processData: openBankingUrl.data,
              selectedAspsp: ipko,
            },
          });

        if (paramQuery.get(openConnectorParam) === VerificationType.Scrapper) {
          history.replace({
            search: `?${verificationIdParam}=${verificationId}`,
          });
        } else {
          dispatch({ type: ActionType.SetUserReturningToProcess });
        }
      } else {
        window.open(openBankingUrl.data.openBankingUrl, '_self');
      }
    }
  }, [openBankingUrl.data]);

  useEffect(() => {
    if (
      aspspList.data &&
      processStatus.data?.verificationStatus ===
      VerificationStatus.AspspsSelected
    ) {
      setOpenBankingUrlQueryEnabled(true);
    }
  }, [aspspList.data, processStatus.data?.verificationStatus]);

  const handleProcessStart = async () => {
    if (selectedAspsp?.id === unknownBankId) {
      setIsUnknownBankPopupVisible(true);
    }
    else {
      setIsProcessingAspspSelect(true);

      let win;
      if (environment.isWebSdk && selectedAspsp?.id !== ipkoId) {
        // Open new window immediately on user interaction so browsers don't block it, adjust location later with backend response
        win = window.open(environment.frontendUrl + LoaderPageRoute);
      }

      if (!selectedAspsp) {
        return setIsProcessingAspspSelect(false);
      }
      try {
        // const selected = await Promise.resolve({
        //   openBankingUrl: 'ws://localhost:1337',
        //   connectorType: VerificationType.Scrapper,
        //   verificationId: '123123',
        // });

        const selected = await sendAspspMutation.mutateAsync({
          aspspId: selectedAspsp.id,
          verificationId: verificationId!,
          otherBank: '',
        });
        dispatch({ type: ActionType.SetProcessData, payload: selected });

        if (selected?.connectorType === VerificationType.Scrapper) {
          dispatch({ type: ActionType.OpenScreenScrape });
          setIsProcessingAspspSelect(false);
        } else {
          if (environment.isWebSdk && win) {
            win.location.href = selected.openBankingUrl;
            setOpenedWindow(win);
            setIsLoginProcessing(true);
            setIsProcessingAspspSelect(false);
          } else {
            queryClient
              .refetchQueries({
                queryKey: verificationQueryKeys.status(verificationId),
              })
              .then(() => {
                window.open(selected.openBankingUrl, '_self');
                //Timeout to fix 'lag' on window.open
                setTimeout(() => {
                  setIsProcessingAspspSelect(false);
                }, 2000);
              });
          }
        }
      } catch (err) {
        setIsProcessingAspspSelect(false);
      }
    }
  };

  return (
    <LayoutContainer>
      {processData?.connectorType === VerificationType.Scrapper && (
        <ScreenScrape
          processData={processData}
          open={isScreenScrapeModalOpen}
          aspsp={selectedAspsp}
          isUserReturningToProcess={isUserReturningToProcess}
          onClose={() => {
            dispatch({ type: ActionType.CloseScreenScrape });
            if (paramQuery.get(openConnectorParam)) {
              history.replace({
                search: `?${verificationIdParam}=${verificationId}`,
              });
            }
          }}
        />
      )}
      <AspspPageContent>
        <Logo />
        <AspspSdkVerificationLoader isLoading={isLoginProcessing}>
        <Heading headingMsgId="aspsp.title" />
        <AspspAlert />
        {selectedAspsp?.id === unknownBankId && unknownBankPopupVisible && (
          <UnknownBankWarning />
        )}
        <ListWrapper>
          {aspspList.isLoading || processStatus.isLoading
            ? range(0, 18).map((n) => <AspspListItemSkeleton key={n} />)
            : aspspList.data?.map((a) => (
              <AspspListItem
                key={a.id}
                aspsp={a}
                isAvailable={
                  !a.health ||
                  [HealthStatus.LIVE, HealthStatus.NO_CALLS].includes(
                    a.health.aisStatus
                  )
                }
                selected={selectedAspsp?.id === a.id}
                isLoading={sendAspspMutation.isLoading}
                onClick={() => {
                  dispatch({ type: ActionType.SelectAspsp, payload: a });
                }}
              />
            ))}
        </ListWrapper>
        <BackToPartnerPageButton />
        <AspspSectionSeparator />
        <AspspControlWrapper>
          {hasIntlMessage('aspsp.nextStep') && (
            <AgreementWrapper>
              <SmallHeading>
                <FormattedMessage id="aspsp.nextStep" />
              </SmallHeading>
              <SecondaryText>
                <FormattedMessage
                  id={
                    selectedAspsp
                      ? 'aspsp.nextStep.clickNext'
                      : 'aspsp.nextStep.chooseBank'
                  }
                />
              </SecondaryText>
              {frontType === 'IDENTITY' ? <SecondaryText><FormattedMessage id="return.information.message" /></SecondaryText> : <></>}
            </AgreementWrapper>
          )}

          {frontType === 'IDENTITY' ? <TestWrapper><SecondaryText><FormattedMessage id="return.information.message" /></SecondaryText></TestWrapper> : <></>}

          <StyledCTAButton
            onClick={
              handleProcessStart}
            disabled={
              !selectedAspsp || isProcessingAspspSelect
            }
            $showOnMobile={!!selectedAspsp}
          >
            {!isProcessingAspspSelect ? (
              <FormattedMessage id="common.next" />
            ) : (
              <Loader width={30} height={30} />
            )}
          </StyledCTAButton>
        </AspspControlWrapper>
        </AspspSdkVerificationLoader>
        <Footer />
      </AspspPageContent>
    </LayoutContainer>
  );
};

export default AspspSelectPage;
